<script setup lang="ts">
import { useInternational } from '@/common/locale';
import { usePropertyStore } from '@/common/stores/property';
import { computed } from 'vue';

const { t } = useInternational();
const propertyStore = usePropertyStore();
const customOptions = computed(() => propertyStore.getOptions?.loginOptions());
</script>

<template>
  <div class="loading-screen full-screen">
    <div
      v-if="customOptions?.useLoadingAnimation"
      class="blocks-wrap"
    >
      <div class="block block--1" />
      <div class="block block--2" />
      <div class="block block--3" />
    </div>
    <div class="loading-area">
      <span class="loading-area__text">
        {{ t('MESSAGE.LOADING_SCREEN', '사용자 데이터 초기화 중입니다') }}
        <span
          v-for="i in 3"
          :key="i"
          class="dot"
        >
          .
        </span>
      </span>
      <span class="loading-area__sub">
        {{ t('MESSAGE.LOADING_SCREEN_SUB', '잠시만 기다려 주십시오') }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
@keyframes dot-fade {
  0%,
  100% {
    opacity: 0;
  }

  50% {
    opacity: 1;
  }
}
.full-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--color-w-gray-01);
}

@keyframes push-block-1 {
  0% {
    width: 14px;
    height: 14px;
    transform: translate3d(0, 18px, 0);
  }
  4% {
    width: 14px;
    height: 32px;
    transform: translate3d(0, 0, 0);
  }
  8%,
  25% {
    width: 14px;
    height: 14px;
    transform: translate3d(0, 0, 0);
  }
  29% {
    width: 32px;
    height: 14px;
    transform: translate3d(0, 0, 0);
  }
  33%,
  50% {
    width: 14px;
    height: 14px;
    transform: translate3d(18px, 0, 0);
  }
  54% {
    width: 14px;
    height: 32px;
    transform: translate3d(18px, 0, 0);
  }
  58%,
  75% {
    width: 14px;
    height: 14px;
    transform: translate3d(18px, 18px, 0);
  }
  79% {
    width: 32px;
    height: 14px;
    transform: translate3d(0, 18px, 0);
  }
  83%,
  100% {
    width: 14px;
    height: 14px;
    transform: translate3d(0, 18px, 0);
  }
}

@keyframes push-block-2 {
  0%,
  8.3% {
    width: 14px;
    height: 14px;
    transform: translate3d(18px, 18px, 0);
  }
  12.3% {
    width: 32px;
    height: 14px;
    transform: translate3d(0, 18px, 0);
  }
  16.3%,
  33.3% {
    width: 14px;
    height: 14px;
    transform: translate3d(0, 18px, 0);
  }
  37.3% {
    width: 14px;
    height: 32px;
    transform: translate3d(0, 0, 0);
  }
  41.3%,
  58.3% {
    width: 14px;
    height: 14px;
    transform: translate3d(0, 0, 0);
  }
  62.3% {
    width: 32px;
    height: 14px;
    transform: translate3d(0, 0, 0);
  }
  66.3%,
  83.3% {
    width: 14px;
    height: 14px;
    transform: translate3d(18px, 0, 0);
  }
  87.3% {
    width: 14px;
    height: 32px;
    transform: translate3d(18px, 0, 0);
  }
  91.3%,
  100% {
    width: 14px;
    height: 14px;
    transform: translate3d(18px, 18px, 0);
  }
}

@keyframes push-block-3 {
  0%,
  16.7% {
    width: 14px;
    height: 14px;
    transform: translate3d(18px, 0, 0);
  }
  20.7% {
    width: 14px;
    height: 32px;
    transform: translate3d(18px, 0, 0);
  }
  24.7%,
  41.7% {
    width: 14px;
    height: 14px;
    transform: translate3d(18px, 18px, 0);
  }
  45.7% {
    width: 32px;
    height: 14px;
    transform: translate3d(0, 18px, 0);
  }
  49.7%,
  66.7% {
    width: 14px;
    height: 14px;
    transform: translate3d(0, 18px, 0);
  }
  70.7% {
    width: 14px;
    height: 32px;
    transform: translate3d(0, 0, 0);
  }
  74.7%,
  91.7% {
    width: 14px;
    height: 14px;
    transform: translate3d(0, 0, 0);
  }
  95.7% {
    width: 32px;
    height: 14px;
    transform: translate3d(0, 0, 0);
  }
  99.7%,
  100% {
    width: 14px;
    height: 14px;
    transform: translate3d(18px, 0, 0);
  }
}
.blocks-wrap {
  --block-1-color: var(--color-w-gray-11);
  --block-2-color: var(--color-w-gray-10);
  --block-3-color: var(--color-w-gray-07);

  position: relative;
  width: 32px;
  height: 32px;
  margin-bottom: 14px;
}
.block {
  position: absolute;
  top: 0;
  left: 0;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  will-change: transform, width, height;
}
.block--1 {
  background: var(--block-1-color);
  animation: push-block-1 7s ease-in-out infinite;
}
.block--2 {
  background: var(--block-2-color);
  animation: push-block-2 7s ease-in-out infinite;
}
.block--3 {
  background: var(--block-3-color);
  animation: push-block-3 7s ease-in-out infinite;
}
.loading-area {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;

  &__text {
    display: flex;
    font-size: 16px;
    font-weight: 700;
    color: var(--color-w-gray-11);
  }

  &__sub {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-w-gray-09);
  }
}
.dot {
  opacity: 0;
  animation: dot-fade 1.5s ease-in-out infinite;

  &:nth-child(1) {
    animation-delay: 0s;
  }

  &:nth-child(2) {
    animation-delay: 0.3s;
  }

  &:nth-child(3) {
    animation-delay: 0.6s;
  }
}
</style>
